import {
  Tr,
  Td,
  Badge,
  Icon,
  Tooltip,
  Flex,
  Text,
  Box,
  HStack,
  VStack,
} from "@chakra-ui/react";
import {
  ChevronUpIcon,
  ChevronDownIcon,
  MinusIcon,
  TriangleUpIcon,
  TriangleDownIcon,
} from "@chakra-ui/icons";

// ─── Sector color map ────────────────────────────────────────
const SECTOR_COLORS = {
  "Real Estate": "green",
  Healthcare: "blue",
  "Health Care": "blue",
  "Communication Services": "orange",
  Financials: "cyan",
  "Consumer Staples": "yellow",
  Technology: "purple",
};

// ─── Shared calculations ─────────────────────────────────────
const calcStock = (stock, price) => {
  const totalPrice    = (price || 0) * stock.nr_shares;
  const avgPriceTotal = stock.average_price_share * stock.nr_shares;
  const paidDiv       = Number(stock.paid_div || 0);
  const commission    = Number(stock.commission || 0);

  // Capital P/L — unrealized gain/loss from price movement
  const capitalPL    = totalPrice - avgPriceTotal;
  const capitalPLPct = avgPriceTotal > 0 ? (capitalPL / avgPriceTotal) * 100 : 0;

  // Dividend P/L — realized return from dividends received
  const dividendPL    = paidDiv;
  const dividendPLPct = avgPriceTotal > 0 ? (dividendPL / avgPriceTotal) * 100 : 0;

  // Total P/L — combined
  const totalPL    = capitalPL + dividendPL;
  const totalPLPct = avgPriceTotal > 0 ? (totalPL / avgPriceTotal) * 100 : 0;

  // Forward-looking dividend metrics
  const annualDivPerShare = stock.div_share * stock.div_per_year;
  const dividendTotal     = annualDivPerShare * stock.nr_shares;
  const dividendYield     = price > 0 ? (annualDivPerShare / price) * 100 : 0;

  // Recovery = dividends as % of initial cost (incl. commission)
  const recover = avgPriceTotal + commission > 0
    ? (paidDiv / (avgPriceTotal + commission)) * 100
    : 0;

  return {
    totalPrice,
    avgPriceTotal,
    capitalPL,
    capitalPLPct,
    dividendPL,
    dividendPLPct,
    totalPL,
    totalPLPct,
    annualDivPerShare,
    dividendTotal,
    dividendYield,
    recover,
  };
};

// ─── Mobile Card ─────────────────────────────────────────────
const StockCard = ({ stock, price, isLoading, weight, c }) => {
  const isProfit    = c.totalPL >= 0;
  const capitalUp   = c.capitalPL >= 0;

  return (
    <Box
      p={3}
      bg="rgba(255,255,255,0.03)"
      borderRadius="xl"
      border="1px solid rgba(255,255,255,0.08)"
      transition="all 0.25s"
      _hover={{
        bg: "rgba(255,255,255,0.06)",
        transform: "translateY(-2px)",
        boxShadow: "0 8px 20px rgba(0,0,0,0.3)",
      }}
    >
      {/* Header: Company + Total P/L */}
      <HStack justify="space-between" mb={2}>
        <VStack align="start" spacing={0} flex={1} minW={0}>
          <Text fontSize="sm" fontWeight="bold" color="teal.300" noOfLines={1}>
            {stock.company}
          </Text>
          <HStack spacing={1.5} mt={0.5}>
            <Badge colorScheme="purple" fontSize="2xs" px={1.5} borderRadius="md">
              {stock.ticker}
            </Badge>
            <Badge
              colorScheme={SECTOR_COLORS[stock.sector] || "gray"}
              fontSize="2xs"
              px={1.5}
              borderRadius="full"
            >
              {stock.sector || "N/A"}
            </Badge>
          </HStack>
        </VStack>

        <VStack spacing={0} align="end" flexShrink={0}>
          <Text fontSize="2xs" color="gray.500">Total P/L</Text>
          <HStack spacing={1}>
            <Icon
              as={isProfit ? TriangleUpIcon : TriangleDownIcon}
              color={isProfit ? "green.400" : "red.400"}
              boxSize={3}
            />
            <Text
              fontSize="lg"
              fontWeight="black"
              color={isProfit ? "green.300" : "red.300"}
            >
              {c.totalPLPct.toFixed(1)}%
            </Text>
          </HStack>
          <Text fontSize="2xs" color={isProfit ? "green.500" : "red.500"}>
            {isProfit ? "+" : ""}${c.totalPL.toFixed(2)}
          </Text>
        </VStack>
      </HStack>

      {/* P/L breakdown: capital vs dividend */}
      <HStack justify="space-between" mb={2} fontSize="2xs" px={1}>
        <Box>
          <Text color="gray.500">Capital</Text>
          <Text color={capitalUp ? "green.300" : "red.300"} fontWeight="semibold">
            {capitalUp ? "+" : ""}${c.capitalPL.toFixed(2)} ({capitalUp ? "+" : ""}{c.capitalPLPct.toFixed(1)}%)
          </Text>
        </Box>
        <Box textAlign="right">
          <Text color="gray.500">Dividends</Text>
          <Text color="yellow.300" fontWeight="semibold">
            ${c.dividendPL.toFixed(2)} ({c.recover.toFixed(1)}% rec)
          </Text>
        </Box>
      </HStack>

      {/* Key metrics 2x2 */}
      <Box bg="whiteAlpha.50" borderRadius="lg" p={2} mb={2}>
        <Flex wrap="wrap">
          <Box w="50%" p={1}>
            <Text fontSize="2xs" color="gray.500">Value</Text>
            <Text fontSize="sm" fontWeight="bold" color="blue.300">
              {isLoading ? "..." : `$${c.totalPrice.toFixed(2)}`}
            </Text>
          </Box>
          <Box w="50%" p={1}>
            <Text fontSize="2xs" color="gray.500">Price</Text>
            <Text fontSize="sm" fontWeight="bold" color="white">
              {isLoading ? "..." : `$${price ? price.toFixed(2) : "0.00"}`}
            </Text>
          </Box>
          <Box w="50%" p={1}>
            <Text fontSize="2xs" color="gray.500">Shares</Text>
            <Text fontSize="sm" fontWeight="medium" color="white">
              {stock.nr_shares}
            </Text>
          </Box>
          <Box w="50%" p={1}>
            <Text fontSize="2xs" color="gray.500">Avg/Share</Text>
            <Text fontSize="sm" fontWeight="medium" color="white">
              ${stock.average_price_share}
            </Text>
          </Box>
        </Flex>
      </Box>

      {/* Bottom badges */}
      <HStack spacing={1.5} flexWrap="wrap">
        <Badge
          colorScheme={weight >= 30 ? "red" : weight >= 20 ? "yellow" : "teal"}
          fontSize="2xs" px={1.5} py={0.5} borderRadius="md"
        >
          W: {weight.toFixed(1)}%
        </Badge>
        <Badge
          colorScheme={c.dividendYield >= 5 ? "green" : c.dividendYield >= 3 ? "yellow" : "gray"}
          fontSize="2xs" px={1.5} py={0.5} borderRadius="md"
        >
          Y: {c.dividendYield.toFixed(1)}%
        </Badge>
        <Badge colorScheme="green" fontSize="2xs" px={1.5} py={0.5} borderRadius="md">
          ${c.dividendTotal.toFixed(2)}/yr
        </Badge>
      </HStack>
    </Box>
  );
};

// ─── Desktop Table Row ───────────────────────────────────────
const StockRow = ({ stock, price, isLoading, weight, c }) => (
  <Tr
    _hover={{
      bg: "gray.700",
      transform: "scale(1.002)",
      boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
    }}
    transition="all 0.2s ease"
    borderBottom="1px solid rgba(255, 255, 255, 0.1)"
  >
    <Td textAlign="center" fontWeight="semibold" color="teal.300">
      {stock.company}
    </Td>

    <Td textAlign="center">
      <Badge colorScheme="purple" fontSize="xs" px={2} py={1} borderRadius="md">
        {stock.ticker}
      </Badge>
    </Td>

    <Td textAlign="center">
      <Badge
        colorScheme={SECTOR_COLORS[stock.sector] || "gray"}
        fontSize="2xs" px={2} py={0.5} borderRadius="full" fontWeight="semibold"
      >
        {stock.sector || "N/A"}
      </Badge>
    </Td>

    <Td textAlign="center" fontSize="sm">{stock.nr_shares}</Td>

    <Td textAlign="center" fontWeight="medium" fontSize="sm">
      {isLoading ? <Text color="gray.500">...</Text> : `$${price ? price.toFixed(2) : "0.00"}`}
    </Td>

    <Td textAlign="center" fontWeight="bold" color="blue.300" fontSize="sm">
      ${c.totalPrice ? c.totalPrice.toFixed(2) : "0.00"}
    </Td>

    <Td textAlign="center">
      <Tooltip label={`This stock represents ${weight.toFixed(2)}% of the total portfolio value`} hasArrow placement="top">
        <Badge colorScheme={weight >= 30 ? "red" : weight >= 20 ? "yellow" : "teal"} fontSize="xs" px={2} py={1} borderRadius="md" fontWeight="bold">
          {weight.toFixed(1)}%
        </Badge>
      </Tooltip>
    </Td>

    <Td textAlign="center" fontSize="sm">${stock.average_price_share}</Td>

    <Td textAlign="center" color="gray.400" fontSize="sm">
      ${c.avgPriceTotal ? c.avgPriceTotal.toFixed(2) : "0.00"}
    </Td>

    {/* Capital P/L — unrealized price movement */}
    <Td textAlign="center">
      <Tooltip
        label={`Capital P/L (unrealized): current value $${c.totalPrice.toFixed(2)} − cost $${c.avgPriceTotal.toFixed(2)}`}
        hasArrow placement="top"
      >
        <VStack spacing={0}>
          <HStack spacing={1}>
            <Icon
              as={c.capitalPL >= 0 ? TriangleUpIcon : TriangleDownIcon}
              color={c.capitalPL >= 0 ? "green.400" : "red.400"}
              boxSize={2.5}
            />
            <Text fontSize="sm" fontWeight="bold" color={c.capitalPL >= 0 ? "green.300" : "red.300"}>
              ${Math.abs(c.capitalPL).toFixed(2)}
            </Text>
          </HStack>
          <Text fontSize="2xs" color={c.capitalPL >= 0 ? "green.500" : "red.500"}>
            {c.capitalPL >= 0 ? "+" : "−"}{Math.abs(c.capitalPLPct).toFixed(2)}%
          </Text>
        </VStack>
      </Tooltip>
    </Td>

    {/* Dividend P/L — realized dividends received + recovery */}
    <Td textAlign="center">
      <Tooltip
        label={`Dividend P/L (realized): $${c.dividendPL.toFixed(2)} received so far, which has recovered ${c.recover.toFixed(2)}% of the initial cost`}
        hasArrow placement="top"
      >
        <VStack spacing={0}>
          <Text fontSize="sm" fontWeight="bold" color="yellow.300">
            ${c.dividendPL.toFixed(2)}
          </Text>
          <Text
            fontSize="2xs"
            color={c.recover >= 100 ? "green.400" : c.recover >= 50 ? "yellow.500" : "gray.500"}
          >
            {c.recover.toFixed(2)}% rec
          </Text>
        </VStack>
      </Tooltip>
    </Td>

    {/* Total P/L — capital + dividends combined */}
    <Td textAlign="center">
      <Tooltip
        label={`Total P/L = Capital ${c.capitalPL >= 0 ? "+" : "−"}$${Math.abs(c.capitalPL).toFixed(2)} + Dividends $${c.dividendPL.toFixed(2)} = $${c.totalPL.toFixed(2)}`}
        hasArrow placement="top"
      >
        <Badge
          bgGradient={
            c.totalPL > 0
              ? "linear(to-r, green.400, green.600)"
              : c.totalPL < 0
              ? "linear(to-r, red.400, red.600)"
              : "linear(to-r, gray.300, gray.400)"
          }
          color="white" fontSize="0.7em" px={2.5} py={1} borderRadius="lg" boxShadow="md"
          display="inline-flex" alignItems="center" gap={1}
        >
          <Icon
            as={c.totalPL > 0 ? ChevronUpIcon : c.totalPL < 0 ? ChevronDownIcon : MinusIcon}
            boxSize={3}
          />
          <VStack spacing={0} align="start">
            <Text lineHeight={1} fontWeight="bold">${Math.abs(c.totalPL).toFixed(2)}</Text>
            <Text lineHeight={1} fontSize="0.85em">
              {c.totalPL >= 0 ? "+" : "−"}{Math.abs(c.totalPLPct).toFixed(2)}%
            </Text>
          </VStack>
        </Badge>
      </Tooltip>
    </Td>

    <Td textAlign="center">
      <Tooltip label={`Annual dividend yield based on current price ($${c.annualDivPerShare.toFixed(2)} / $${price ? price.toFixed(2) : "0"})`} hasArrow placement="top">
        <Badge colorScheme={c.dividendYield >= 5 ? "green" : c.dividendYield >= 3 ? "yellow" : "gray"} fontSize="xs" px={2} py={1} borderRadius="md" fontWeight="bold">
          {c.dividendYield.toFixed(2)}%
        </Badge>
      </Tooltip>
    </Td>

    <Td textAlign="center" fontSize="sm">${stock.div_share}</Td>

    <Td textAlign="center" fontWeight="semibold" color="green.300" fontSize="sm">
      <Tooltip label="Expected annual dividend income from this stock" hasArrow placement="top">
        ${c.dividendTotal ? c.dividendTotal.toFixed(2) : "0.00"}
      </Tooltip>
    </Td>

    <Td textAlign="center" color="gray.500" fontSize="sm">${stock.commission}</Td>
  </Tr>
);

// ─── Main Export ─────────────────────────────────────────────
const Stock = ({ stock, price = null, isLoading = false, weight = 0, isMobile = false }) => {
  const c = calcStock(stock, price);

  if (isMobile) {
    return <StockCard stock={stock} price={price} isLoading={isLoading} weight={weight} c={c} />;
  }

  return <StockRow stock={stock} price={price} isLoading={isLoading} weight={weight} c={c} />;
};

export default Stock;